import { useState } from 'react';
import {
  Container,
  Stack,
  Title,
  Button,
  Group,
  Text,
  ActionIcon,
  TextInput,
  NumberInput,
  Modal,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconPlus,
  IconRefresh,
  IconSearch,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MobileChildCard } from './MobileChildCard';
import { parentApi } from '@/features/parent/api/parentApi';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useAddChild } from '@/features/parent/hooks/useParent';

export function MobileChildrenListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [searchQuery, setSearchQuery] = useState('');
  const [addChildModalOpen, setAddChildModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const { data: children, isLoading, refetch } = useQuery({
    queryKey: ['children', user?.id],
    queryFn: () => parentApi.getChildren(),
    enabled: !!user?.id,
  });

  const addChild = useAddChild();

  const form = useForm({
    initialValues: {
      first_name: '',
      last_name: '',
      display_name: '',
      grade_level: null as number | null,
    },
    validate: {
      first_name: (v) => (!v ? 'First name is required' : null),
      last_name: (v) => (!v ? 'Last name is required' : null),
      display_name: (v) => (!v ? 'Display name is required' : null),
    },
  });

  const handleAdd = form.onSubmit((values) => {
    addChild.mutate(values, {
      onSuccess: () => {
        form.reset();
        setAddChildModalOpen(false);
      },
    });
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const filteredChildren = children?.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <Container size="sm" p="md">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>My Children</Title>
          <Group gap="xs">
            <ActionIcon onClick={handleRefresh} loading={refreshing} variant="subtle" size="lg">
              <IconRefresh size={20} />
            </ActionIcon>
            <Button
              leftSection={<IconPlus size={16} />}
              size="xs"
              onClick={() => setAddChildModalOpen(true)}
            >
              Add Child
            </Button>
          </Group>
        </Group>

        {children && children.length > 1 && (
          <TextInput
            placeholder="Search children..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        )}

        {isLoading ? (
          <Text size="sm" c="dimmed" ta="center">
            Loading children...
          </Text>
        ) : filteredChildren && filteredChildren.length > 0 ? (
          <Stack gap="md">
            {filteredChildren.map((child) => (
              <MobileChildCard
                key={child.id}
                child={child}
                onViewDetails={() => navigate(`/mobile/parent/child/${child.id}`)}
                onQuickAssign={() => navigate(`/mobile/parent/assign/story?childId=${child.id}`)}
              />
            ))}
          </Stack>
        ) : searchQuery ? (
          <Text size="sm" c="dimmed" ta="center">
            No children found matching "{searchQuery}"
          </Text>
        ) : (
          <Stack align="center" gap="lg" mt={40}>
            <Text size="sm" c="dimmed" ta="center">
              No children added yet
            </Text>
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => setAddChildModalOpen(true)}
            >
              Add Your First Child
            </Button>
          </Stack>
        )}
      </Stack>

      {/* Add Child Modal */}
      <Modal
        opened={addChildModalOpen}
        onClose={() => {
          setAddChildModalOpen(false);
          form.reset();
        }}
        title="Add Child"
        size="md"
      >
        <form onSubmit={handleAdd}>
          <Stack gap="sm">
            <TextInput
              label="First name"
              placeholder="First name"
              required
              {...form.getInputProps('first_name')}
            />
            <TextInput
              label="Last name"
              placeholder="Last name"
              required
              {...form.getInputProps('last_name')}
            />
            <TextInput
              label="Display name"
              placeholder="How should we address them?"
              required
              {...form.getInputProps('display_name')}
            />
            <NumberInput
              label="Grade"
              placeholder="Grade"
              min={1}
              max={12}
              {...form.getInputProps('grade_level')}
            />
            <Group justify="flex-end" mt="sm">
              <Button
                variant="subtle"
                onClick={() => {
                  setAddChildModalOpen(false);
                  form.reset();
                }}
              >
                Cancel
              </Button>
              <Button type="submit" loading={addChild.isPending}>
                Add
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

    </Container>
  );
}
